import type { IDeviceControlOption } from '@/Interfaces/Device.interface';
import LineItemDeviceService from '@/Services/CampaignManagement/Device.service';
import ToggleButton from '@/components/Buttons/ToggleButton';
import FormErrorHelperText from '@/components/Form/FormErrorHelperText';
import { deviceIdToControlOption } from '@/helpers/devices';
import type { LineItemChannel } from '@/types/LineItem/channel.types';
import { FormLabel } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DeviceIcons } from './constants';

const DeviceSection = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [watchChannelOption, watchDevices]: [{ label: LineItemChannel } | null, IDeviceControlOption[]] = useWatch({
    control,
    name: ['channel', 'devices'],
  });
  const watchChannel = watchChannelOption?.label;

  const error = useMemo(() => errors.devices, [errors.devices]);

  const getIsSelected = useCallback(
    (device: IDeviceControlOption) => watchDevices.some(option => device.value === option.value),
    [watchDevices],
  );

  const onDevicesChange = useCallback(
    (option: IDeviceControlOption) => () => {
      if (!getIsSelected(option)) {
        return setValue('devices', [...watchDevices, option], { shouldDirty: true, shouldValidate: true });
      }
      return setValue(
        'devices',
        [...watchDevices].filter(v => v.value !== option.value),
        { shouldDirty: true, shouldValidate: true },
      );
    },
    [setValue, watchDevices],
  );

  return (
    <div className='flex flex-1 flex-col'>
      <FormLabel aria-label='Devices'>Devices</FormLabel>
      <div className='flex flex-nowrap gap-x-2 gap-y-2 h-[32px]'>
        {watchChannel &&
          LineItemDeviceService.channelDevices[watchChannel]?.map(deviceId => {
            const Icon = DeviceIcons[deviceId];
            const option = deviceIdToControlOption(deviceId);
            return (
              <ToggleButton
                key={deviceId}
                label={option.label}
                value={option.value}
                startAdornment={Icon ? <Icon width='20' /> : null}
                selected={getIsSelected(option)}
                onClick={onDevicesChange(option)}
              />
            );
          })}
      </div>
      {error ? <FormErrorHelperText error>{error?.message}</FormErrorHelperText> : null}
    </div>
  );
};

export default DeviceSection;
